import axios from 'axios';
import {
  ChatRoom,
  ChatMessage,
  CreateRoomDto,
  SendMessageDto
} from '../types/chat.types';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:3010';
const API_BASE = `${BASE_URL}/chat`;
const API_URL = BASE_URL;

// Get auth token from your existing auth system
const getAuthToken = () => {
  return localStorage.getItem('accessToken') || '';
};

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const chatApi = {
  async addParticipant(roomId: string, userId: string): Promise<void> {
    await api.post(`/rooms/${roomId}/participants`, { userId });
  },

  async createRoom(dto: CreateRoomDto): Promise<ChatRoom> {
    const { data } = await api.post('/rooms', dto);
    return data;
  },

  async deleteMessage(messageId: string): Promise<void> {
    await api.delete(`/messages/${messageId}`);
  },

  async deleteRoom(roomId: string): Promise<void> {
    await api.delete(`/rooms/${roomId}`);
  },

  async editMessage(
    messageId: string,
    messageText: string
  ): Promise<ChatMessage> {
    const { data } = await api.put(`/messages/${messageId}`, { messageText });
    return data;
  },

  // Messages
  async getMessages(
    roomId: string,
    before?: Date,
    lang = 'en',
    limit = 50
  ): Promise<ChatMessage[]> {
    const params: any = { lang, limit };
    if (before) params.before = before.toISOString();

    const { data } = await api.get(`/rooms/${roomId}/messages`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Expires: '0',
        Pragma: 'no-cache'
      },
      params
    });
    return data;
  },

  async getRoomDetails(roomId: string): Promise<ChatRoom> {
    const { data } = await api.get(`/rooms/${roomId}`);
    return data;
  },

  // Rooms
  async getRooms(): Promise<ChatRoom[]> {
    const { data } = await api.get('/rooms');
    return data;
  },

  // Users (uses root API, not /chat prefix)
  async getUsers(): Promise<any[]> {
    const { data } = await axios.get(`${BASE_URL}/users`, {
      headers: {
        Authorization: `Bearer ${getAuthToken()}`
      }
    });
    return data;
  },

  async markAsRead(messageId: string): Promise<void> {
    await api.post(`/messages/${messageId}/read`);
  },

  async markRoomAsRead(roomId: string): Promise<void> {
    await api.put(`/rooms/${roomId}/read`);
  },

  async removeParticipant(roomId: string, userId: string): Promise<void> {
    await api.delete(`/rooms/${roomId}/participants/${userId}`);
  },

  async renameRoom(roomId: string, name: string): Promise<ChatRoom> {
    const { data } = await api.put(`/rooms/${roomId}/rename`, { name });
    return data;
  },

  // Search
  async searchMessages(query: string, roomId?: string): Promise<ChatMessage[]> {
    const params: any = { q: query };
    if (roomId) params.roomId = roomId;

    const { data } = await api.get('/search', { params });
    return data;
  },

  async sendMessage(roomId: string, dto: SendMessageDto): Promise<ChatMessage> {
    const { data } = await api.post(`/rooms/${roomId}/messages`, dto);
    return data;
  },

  // Pin/Unpin room - RENAMED from pinRoom to togglePin
  async togglePin(roomId: string, isPinned: boolean): Promise<void> {
    await api.put(`/rooms/${roomId}/pin`, { isPinned });
  },

  // File upload
  async uploadFile(
    file: File,
    roomId: string,
    messageId: string
  ): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('roomId', roomId);
    formData.append('messageId', messageId);

    const { data } = await api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return data.url;
  }
};

export default chatApi;
